import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useExternalCallFailures,
  useExternalCallOperations,
  useExternalCalls,
  useExternalCallsTimeseries,
  type ExternalCallBucket,
  type ExternalCallStat,
} from "@/features/admin";
import { useMemo, useState } from "react";
import { AdminPageLayout } from "../layout";
import { ExternalCallOperations } from "./components/external-call-operations";
import { groupLabel, ms, pct } from "./components/external-calls-format";
import { StatCard } from "./components/stat-card";
import {
  MAX_SERIES,
  TimeseriesChart,
  type TimeseriesSeries,
} from "./components/timeseries-chart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const WINDOWS = [
  { hours: 1, label: "Dernière heure" },
  { hours: 24, label: "24 dernières heures" },
  { hours: 24 * 7, label: "7 derniers jours" },
  { hours: 24 * 30, label: "30 derniers jours" },
];

/** Errors: green under 1%, amber under 5%, red beyond — a provider at 5% is an incident. */
const errorTone = (rate: number): "default" | "warn" | "bad" =>
  rate >= 0.05 ? "bad" : rate >= 0.01 ? "warn" : "default";

/** Latency: these calls sit inside a live conversation, so the bar is low. */
const latencyTone = (n: number): "default" | "warn" | "bad" =>
  n >= 3000 ? "bad" : n >= 1000 ? "warn" : "default";

const when = (t: number) =>
  t
    ? new Date(t).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/**
 * Turn the hourly buckets into one line per series key (integration, or target once an
 * integration is selected). Hours with no traffic stay absent so the line breaks instead of
 * dropping to zero — no calls is not "instant calls".
 */
const seriesOf = (
  buckets: ExternalCallBucket[],
  keyOf: (b: ExternalCallBucket) => string,
  valueOf: (b: ExternalCallBucket) => number,
): TimeseriesSeries[] => {
  const byKey = new Map<string, { x: number; y: number | null }[]>();
  for (const bucket of buckets) {
    const key = keyOf(bucket) || "—";
    const points = byKey.get(key) || [];
    points.push({ x: bucket.bucket_start, y: valueOf(bucket) });
    byKey.set(key, points);
  }
  return [...byKey.entries()]
    .sort((a, b) => b[1].length - a[1].length)
    .slice(0, MAX_SERIES)
    .map(([id, data], index) => ({
      id,
      colorIndex: index,
      data: data.sort((a, b) => a.x - b.x),
    }));
};

/**
 * Monitoring of the outbound calls the platform makes to third parties.
 *
 * The technical pages next door measure *us* (STT/LLM/TTS). This one measures everyone we
 * depend on mid-call: the booking adapters, the tenants' own APIs, the MCP servers, and the
 * websites the browser agent drives. Grouped by integration first, then by target — host for
 * the HTTP adapters, **domain** for the browser, which has no fixed provider of its own.
 */
export function AdminExternalCallsPage() {
  const [hours, setHours] = useState(24);
  const [group, setGroup] = useState<string | undefined>();

  const { data, isLoading } = useExternalCalls(hours, { group });
  const { data: buckets = [], isLoading: loadingSeries } =
    useExternalCallsTimeseries(hours, { group });
  const { data: operations = [] } = useExternalCallOperations(hours, { group });
  const { data: failures = [] } = useExternalCallFailures(hours, {
    group,
    limit: 100,
  });

  // Without a selected integration the series are per integration; inside one, per target —
  // the same drill-down the tables follow.
  const keyOf = group
    ? (b: ExternalCallBucket) => b.target
    : (b: ExternalCallBucket) => groupLabel(b.api_group);

  const volume = useMemo(
    () => seriesOf(buckets, keyOf, (b) => b.calls),
    [buckets, group],
  );
  const latency = useMemo(
    () => seriesOf(buckets, keyOf, (b) => b.p95_ms),
    [buckets, group],
  );
  const errors = useMemo(
    () => seriesOf(buckets, keyOf, (b) => b.errors),
    [buckets, group],
  );

  const totals = data?.totals;

  return (
    <AdminPageLayout
      title="Appels API externes"
      description={
        totals
          ? `${totals.calls} appel(s) sortant(s) sur la période, ${totals.errors} en erreur`
          : "Latence et erreurs des systèmes tiers appelés pendant les conversations"
      }
      actions={[
        <Select
          key="window"
          value={String(hours)}
          onValueChange={(v) => setHours(Number(v))}
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOWS.map((w) => (
              <SelectItem key={w.hours} value={String(w.hours)}>
                {w.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>,
      ]}
    >

      <div className="grid grid-cols-4 gap-4">
        <StatCard
          title="Appels"
          value={totals ? totals.calls.toLocaleString("fr-FR") : "—"}
          isLoading={isLoading}
        />
        <StatCard
          title="Taux d'erreur"
          value={totals ? pct(totals.error_rate) : "—"}
          hint={totals ? `${totals.errors} échec(s)` : undefined}
          isLoading={isLoading}
          tone={totals ? errorTone(totals.error_rate) : "default"}
        />
        <StatCard
          title="Temps de réponse p50"
          value={totals ? ms(totals.p50_ms) : "—"}
          isLoading={isLoading}
          tone={totals ? latencyTone(totals.p50_ms) : "default"}
        />
        <StatCard
          title="Temps de réponse p95"
          value={totals ? ms(totals.p95_ms) : "—"}
          hint="Les pics, ceux que l'appelant entend"
          isLoading={isLoading}
          tone={totals ? latencyTone(totals.p95_ms) : "default"}
        />
      </div>

      {group ? (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Filtré sur</span>
          <span className="font-medium">{groupLabel(group)}</span>
          <Button variant="ghost" size="sm" onClick={() => setGroup(undefined)}>
            Tout afficher
          </Button>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-4">
        <TimeseriesChart
          title="Volume d'appels par heure"
          description={
            group
              ? "Par cible (hôte ou domaine)"
              : "Par intégration — cliquez une ligne du tableau pour entrer dedans"
          }
          series={volume}
          format={(v) => `${Math.round(v)}`}
          isLoading={loadingSeries}
        />
        <TimeseriesChart
          title="Temps de réponse p95 par heure"
          description="Ce que l'agent attend avant de pouvoir répondre"
          series={latency}
          format={ms}
          isLoading={loadingSeries}
        />
      </div>

      <TimeseriesChart
        title="Erreurs par heure"
        description="Timeouts et réponses non exploitables (4xx/5xx, réseau)"
        series={errors}
        format={(v) => `${Math.round(v)}`}
        isLoading={loadingSeries}
        height={220}
      />

      <StatTable
        title="Par intégration (tool / adapter)"
        rows={data?.groups || []}
        label={(row) => groupLabel(row.api_group)}
        onSelect={(row) => setGroup(row.api_group)}
        selected={group}
      />

      <StatTable
        title={
          group
            ? "Par cible dans l'intégration sélectionnée"
            : "Par cible — hôte pour les APIs, domaine pour le navigateur"
        }
        rows={data?.targets || []}
        label={(row) => row.target || "—"}
        sublabel={(row) => groupLabel(row.api_group)}
      />

      <ExternalCallOperations operations={operations} group={group} />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Dernières erreurs</CardTitle>
          <p className="text-xs text-muted-foreground">
            Les messages sont tronqués et ne contiennent jamais le corps des
            réponses.
          </p>
        </CardHeader>
        <CardContent>
          {failures.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aucune erreur sur la période.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Heure</TableHead>
                  <TableHead>Intégration</TableHead>
                  <TableHead>Cible</TableHead>
                  <TableHead>Opération</TableHead>
                  <TableHead className="text-right">Statut</TableHead>
                  <TableHead>Erreur</TableHead>
                  <TableHead className="text-right">Durée</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failures.map((f, index) => (
                  <TableRow key={`${f.started_at}-${index}`}>
                    <TableCell className="whitespace-nowrap">
                      {when(f.started_at)}
                    </TableCell>
                    <TableCell>{groupLabel(f.api_group)}</TableCell>
                    <TableCell className="text-muted-foreground">{f.target}</TableCell>
                    <TableCell>{f.operation}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {f.status_code || f.error_type}
                    </TableCell>
                    <TableCell
                      className="max-w-[320px] truncate"
                      title={f.error_message}
                    >
                      {f.error_message || f.error_type}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {ms(f.duration_ms)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}

/** Shared stats table — same columns at every depth, so the numbers stay comparable. */
function StatTable({
  title,
  rows,
  label,
  sublabel,
  onSelect,
  selected,
}: {
  title: string;
  rows: ExternalCallStat[];
  label: (row: ExternalCallStat) => string;
  sublabel?: (row: ExternalCallStat) => string;
  onSelect?: (row: ExternalCallStat) => void;
  selected?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucun appel sur la période.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead className="text-right">Appels</TableHead>
                <TableHead className="text-right">Erreurs</TableHead>
                <TableHead className="text-right">Timeouts</TableHead>
                <TableHead className="text-right">p50</TableHead>
                <TableHead className="text-right">p95</TableHead>
                <TableHead className="text-right">Max</TableHead>
                <TableHead className="text-right">Dernier appel</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={`${row.api_group}:${row.target ?? ""}`}
                  className={`border-t ${
                    onSelect ? "cursor-pointer hover:bg-muted/50" : ""
                  } ${
                    selected && selected === row.api_group ? "bg-muted/50" : ""
                  }`}
                  onClick={onSelect ? () => onSelect(row) : undefined}
                >
                  <TableCell>
                    <div className="font-medium">{label(row)}</div>
                    {sublabel ? (
                      <div className="text-xs text-muted-foreground">
                        {sublabel(row)}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.calls}</TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${
                      errorTone(row.error_rate) === "bad"
                        ? "text-red-600 dark:text-red-400"
                        : errorTone(row.error_rate) === "warn"
                          ? "text-amber-600 dark:text-amber-400"
                          : ""
                    }`}
                  >
                    {row.errors ? `${pct(row.error_rate)} (${row.errors})` : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.timeouts || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {ms(row.p50_ms)}
                  </TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${
                      latencyTone(row.p95_ms) === "bad"
                        ? "text-red-600 dark:text-red-400"
                        : latencyTone(row.p95_ms) === "warn"
                          ? "text-amber-600 dark:text-amber-400"
                          : ""
                    }`}
                  >
                    {ms(row.p95_ms)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {ms(row.max_ms)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground whitespace-nowrap">
                    {when(row.last_call_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
